<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { dateFormat } from 'src/helpers/date_helper';
	import type { CloudRecording } from '../../types/cloud-recordings';
	import {
		AdvancedCardV2,
		DropdownMenu,
		MenuItemList,
		IconButton,
		LabelListV2,
		Icon,
		toast,
	} from '@Shelly/shelly-design-system/index';
	import {
		getRecordingEventLabel,
		downloadMedia,
		isDownloadableMedia,
	} from '../../helpers/camera-recordings';
	import CameraThumbnail from '../../CameraThumbnail.svelte';
	import AIDetectionModal from './AIDetectionModal.svelte';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';

	export let recording: CloudRecording;
	export let selected: boolean = false;
	export let variant: 'simple' | 'detailed' = 'detailed';

	// Only the home owner may delete recordings; recipients of a shared home cannot.
	$: isSharedHome = isActiveHomeShared();

	$: showContextMenu = false;
	let showAIDetectionModal = false;
	let isDownloading = false;

	const dispatch = createEventDispatcher();

	const handleClick = () => {
		dispatch('select', recording);
	};

	const handleContextClick = (e: MouseEvent) => {
		e.stopPropagation();
		showContextMenu = !showContextMenu;
	};

	const handleDelete = (recording: { id: string }) => {
		showContextMenu = false;
		dispatch('delete', recording);
	};

	const handleDownload = async () => {
		showContextMenu = false;
		isDownloading = true;
		const result = await downloadMedia(recording);
		if (result.reason === 'error') {
			toast.open({
				type: 'danger',
				title: $format('camera_recording_download_error', {
					default: 'Failed to download',
				}),
			});
		}
		isDownloading = false;
	};
</script>

<AdvancedCardV2
	on:click={handleClick}
	{selected}
	title={dateFormat(recording.date, 'd M H:i:s')}
	aria-label={recording.date}
	isFocusable
>
	<CameraThumbnail
		class="mr-xs"
		borderRadius="small"
		aspectRatio="16-9"
		size="preview-s"
		slot="left"
		deviceId={recording.mac}
		recId={recording.id}
		recDate={recording.date}
		src={recording.imageUrls?.small}
		imageSize="small"
		encrypted={!!recording.encrypted}
	/>
	<LabelListV2
		size="s"
		labelList={getRecordingEventLabel(recording)}
		slot="main"
		on:label-click={handleClick}
		a11yProps={{ isInteractive: true }}
		data-prevent-drawer-swipe
		data-prevent-sidepanel-swipe
	/>
	<div slot="right" class="d-flex align-items-center h-100">
		{#if variant === 'detailed'}
			<DropdownMenu show={showContextMenu}>
				<IconButton
					aria-label={$format('button_more_options', {
						default: 'More options',
					})}
					slot="trigger"
					variant="secondary"
					size="s"
					icon="fas fa-ellipsis-vertical"
					class={selected ? '' : 'text-color-2'}
					on:click={(e) => handleContextClick(e)}
				/>
				<MenuItemList
					icon="fas fa-sparkles"
					title={$format('camera_recording_ai_detection', {
						default: 'AI Detection',
					})}
					on:click={() => {
						showContextMenu = false;
						showAIDetectionModal = true;
					}}
					isSelectable={false}
				/>
				{#if isDownloadableMedia(recording)}
					<MenuItemList
						icon="fas fa-download"
						title={$format('camera_recording_download', {
							default: 'Download',
						})}
						disabled={isDownloading}
						on:click={handleDownload}
						isSelectable={false}
					/>
				{/if}
				<MenuItemList
					icon="fas fa-trash text-danger"
					iconStyle="danger"
					title={$format('button_delete', {
						default: 'Delete',
					})}
					disabled={isSharedHome}
					on:click={() => handleDelete(recording)}
					isSelectable={false}
				/>
			</DropdownMenu>
		{:else}
			<Icon icon="fal fa-chevron-right" size="s" />
		{/if}
	</div>
</AdvancedCardV2>

<AIDetectionModal
	showModal={showAIDetectionModal}
	{recording}
	on:close={() => (showAIDetectionModal = false)}
/>
